import { useCallback, useEffect } from 'react';
import { useAuthStore } from '../store/authStore';

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: AppleIDInitConfig) => void;
        signIn: () => Promise<AppleIDSignInResponse>;
      };
    };
  }
}

interface AppleIDInitConfig {
  clientId: string;
  scope: string;
  redirectURI: string;
  state?: string;
  nonce?: string;
  usePopup: boolean;
}

interface AppleIDSignInResponse {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
  user?: {
    email: string;
    name: {
      firstName: string;
      lastName: string;
    };
  };
}

// Apple Services ID for web (different from iOS Bundle ID)
const APPLE_CLIENT_ID = process.env.REACT_APP_APPLE_CLIENT_ID || 'com.koru.web.auth';
// Redirect URI still required even in popup mode
const APPLE_REDIRECT_URI = process.env.REACT_APP_APPLE_REDIRECT_URI || `${window.location.origin}/`;

export const useAppleSignIn = () => {
  const { appleSignIn, isLoading } = useAuthStore();

  const initializeAppleSignIn = useCallback(() => {
    if (!window.AppleID) {
      console.warn('[AppleSignIn Web] Apple Sign-In SDK not loaded');
      return;
    }

    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT_URI,
        usePopup: true, // Use popup mode for static sites
      });
      console.log('[AppleSignIn Web] ✅ Initialized with clientId:', APPLE_CLIENT_ID);
      console.log('[AppleSignIn Web] Redirect URI:', APPLE_REDIRECT_URI);
    } catch (error) {
      console.error('[AppleSignIn Web] ❌ Initialization failed:', error);
    }
  }, []);

  const signInWithApple = useCallback(async () => {
    if (!window.AppleID) {
      console.error('[AppleSignIn Web] ❌ Apple ID SDK not available');
      return;
    }

    try {
      console.log('[AppleSignIn Web] Starting Apple Sign-In with popup...');
      const response = await window.AppleID.auth.signIn();
      console.log('[AppleSignIn Web] ✅ Received response from Apple');

      const { authorization, user } = response;

      // Apple only provides user name/email on first authorization
      const email = user?.email || '';
      const firstName = user?.name?.firstName || '';
      const lastName = user?.name?.lastName || '';

      // Extract Apple User ID and email from the id_token (JWT)
      const idToken = authorization.id_token;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const appleUserId = payload.sub;
      const emailFromToken = payload.email || '';

      console.log('[AppleSignIn Web] Extracted data:', {
        appleUserId,
        email: email || emailFromToken || '(empty)',
        firstName: firstName || '(empty)',
        lastName: lastName || '(empty)',
      });

      // Call the backend
      console.log('[AppleSignIn Web] Calling backend API...');
      await appleSignIn({
        email: email || emailFromToken,
        firstName,
        lastName,
        appleUserId,
        identityToken: idToken,
      });

      console.log('[AppleSignIn Web] ✅ Sign-in complete');
    } catch (error: any) {
      // User canceled or error occurred
      if (error?.error === 'popup_closed_by_user') {
        console.log('[AppleSignIn Web] User canceled sign-in');
        return;
      }
      console.error('[AppleSignIn Web] ❌ Sign-in failed:', error);
    }
  }, [appleSignIn]);

  useEffect(() => {
    // Initialize when Apple SDK is loaded
    const checkAppleLoaded = () => {
      if (window.AppleID) {
        initializeAppleSignIn();
      } else {
        // Retry after a short delay
        setTimeout(checkAppleLoaded, 100);
      }
    };

    const timeoutId = setTimeout(checkAppleLoaded, 100);
    return () => clearTimeout(timeoutId);
  }, [initializeAppleSignIn]);

  return {
    signInWithApple,
    isLoading,
    isAppleLoaded: !!window.AppleID,
  };
};
